import { motion } from "framer-motion";
import { Wind, Waves, Music4, MoonStar, ArrowRight } from "lucide-react";

const EASE = [0.16, 1, 0.3, 1];

const MEDITATIONS = [
  {
    icon: Wind,
    title: "Dynamic Meditation",
    text: "Osho's signature morning practice — five stages moving through breath, catharsis, and celebration, designed to break old patterns and empty the mind for silence to enter.",
    testid: "meditation-dynamic",
  },
  {
    icon: Waves,
    title: "Kundalini Meditation",
    text: "Shaking, dancing, stillness, and rest — a gentle evening practice that shakes the dust of the day from the body and lets energy rise on its own.",
    testid: "meditation-kundalini",
  },
  {
    icon: Music4,
    title: "Nadabrahma Meditation",
    text: "The meditation of sound — soft humming and slow hand movements dissolving, layer by layer, into stillness.",
    testid: "meditation-nadabrahma",
  },
  {
    icon: MoonStar,
    title: "Silent Sits & Satsang",
    text: "Simply sitting in silence — near Hanuman-ji, beneath the Texas sky — with nothing to achieve and nowhere to go.",
    testid: "meditation-silence",
  },
];

export default function OshoMeditations() {
  return (
    <section id="meditations" data-testid="meditations-section" className="py-20 sm:py-28 bg-[#450A0A] text-stone-200 relative overflow-hidden">
      <div className="absolute inset-0 opacity-[0.08] pointer-events-none"
        style={{ backgroundImage: "radial-gradient(circle at 15% 20%, #FCA5A5 0%, transparent 40%), radial-gradient(circle at 85% 85%, #F59E0B 0%, transparent 45%)" }} />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl">
          <p className="eyebrow !text-rose-300 mb-4">08 · The Way of Silence</p>
          <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-rose-50" data-testid="meditations-heading">
            Osho Meditations at the Spiritual Village
          </h2>
          <p className="mt-5 text-stone-300/90 leading-relaxed">
            Osho Kaivalya Dham is not only a mandir — it is a meditation village. Alongside devotion to
            Baba and Hanuman-ji, we keep a space to explore Osho's meditations: in our founder's words,
            <span className="font-quote italic text-rose-200"> "practices that invite awareness, emotional
            expression, inner silence, and a more loving relationship with life."</span>
          </p>
          <p className="mt-4 text-stone-400 leading-relaxed">
            Meditation and devotion have found a home together here. The maroon and the saffron sit side
            by side — and so can you.
          </p>
        </div>

        <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {MEDITATIONS.map((m, i) => (
            <motion.div key={m.title}
              initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }} transition={{ duration: 0.6, delay: i * 0.1, ease: EASE }}
              data-testid={m.testid}
              className="group rounded-2xl border border-rose-200/15 bg-[#380808]/70 backdrop-blur-sm p-7 hover:border-rose-300/40 hover:-translate-y-1.5 hover:shadow-xl hover:shadow-black/30 transition-all duration-300"
            >
              <span className="w-11 h-11 rounded-full bg-rose-500/10 border border-rose-300/20 flex items-center justify-center mb-5 group-hover:scale-110 transition-transform duration-300">
                <m.icon className="h-5 w-5 text-rose-300" />
              </span>
              <h3 className="font-display text-lg text-rose-50">{m.title}</h3>
              <p className="mt-2.5 text-sm leading-relaxed text-stone-400">{m.text}</p>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }} transition={{ duration: 0.8, ease: EASE }}
          className="mt-12 flex flex-wrap items-center justify-between gap-6 rounded-2xl border border-rose-200/15 bg-[#380808]/70 px-7 py-6"
          data-testid="meditations-cta"
        >
          <p className="text-sm text-stone-300 leading-relaxed max-w-xl">
            New to meditation? You are especially welcome. Write to us for the weekly meditation
            schedule — come with an open heart, and experience this place for yourself.
          </p>
          <a href="#visit" data-testid="meditations-contact-cta"
            className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-rose-600 to-amber-600 px-7 py-3 text-sm font-semibold text-white shadow-lg shadow-black/30 hover:-translate-y-0.5 hover:shadow-black/50 transition-all duration-300">
            Ask About Meditation Times <ArrowRight className="h-4 w-4" />
          </a>
        </motion.div>
      </div>
    </section>
  );
}
